/**
 * LearnQuiz.jsx — shared comprehension-quiz block used by every drill.
 *
 * The passage's `questions` array carries only { q, options } — the
 * server holds the answer key so we can't accidentally leak it. Users
 * submit their picks; the server scores. We show a summary card only
 * after the server responds so the reveal ordering is authoritative.
 */
import React, { useState } from 'react';
import { CheckCircle2, XCircle, Loader2, Trophy, Coins, Zap } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnQuiz = ({
  passage,
  drillKind,
  durationMs,
  chunkSize,
  targetWpm,
  metadata,
  // Optional: override which question bank to render. Defaults to
  // `passage.questions`. Vocab drill passes `passage.vocab` here so
  // the same UI can score word/definition pairs.
  questions,
  eyebrow,
  onDone,
}) => {
  const [picks, setPicks] = useState({}); // { qIdx: optionIdx }
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const qBank = questions || passage.questions;
  const total = qBank.length;
  const answered = Object.keys(picks).length;

  const submit = async () => {
    if (submitting || result) return;
    if (answered !== total) {
      setError(`Answer all ${total} questions first.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const answers = Array.from({ length: total }, (_, i) =>
        typeof picks[i] === 'number' ? picks[i] : -1,
      );
      const r = await authFetch(`${BACKEND_URL}/api/learn/session/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          drill_kind: drillKind,
          passage_id: passage.passage_id,
          duration_ms: Math.max(1000, Math.round(durationMs)),
          answers,
          chunk_size: chunkSize,
          target_wpm: targetWpm,
          metadata: metadata || undefined,
        }),
      });
      // Feb 14 2026 — 402 = trial expired. Surface the paywall message
      // with a link over to /premium rather than a raw JSON blob.
      if (r.status === 402) {
        let msg = 'Free trial ended. Upgrade to Premium to keep training.';
        try { const d = await r.json(); if (d?.detail?.message) msg = d.detail.message; } catch { /* no-op */ }
        setError(msg);
        return;
      }
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setResult(d);
      onDone && onDone(d);
    } catch (e) {
      setError(e?.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    const compGood = result.comprehension_pct >= 80;
    return (
      <div
        className="rounded-2xl bg-gradient-to-br from-cyan-950/60 via-slate-900 to-slate-950 border border-cyan-500/25 p-6"
        data-testid="learn-quiz-result"
      >
        <p className="eyebrow text-cyan-300 mb-1">Session complete</p>
        <h2 className="font-display text-3xl font-black text-white tracking-tight mb-4">
          Adjusted WPM:{' '}
          <span className="text-cyan-300 tabular-nums" data-testid="learn-quiz-result-adjusted">
            {Math.round(result.adjusted_wpm)}
          </span>
        </h2>
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="rounded-lg bg-black/30 border border-white/10 p-3">
            <p className="text-[10px] uppercase tracking-widest text-white/50 font-black">Raw WPM</p>
            <p className="text-xl font-black text-white tabular-nums">{Math.round(result.raw_wpm)}</p>
          </div>
          <div className={`rounded-lg border p-3 ${compGood ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-black/30 border-white/10'}`}>
            <p className="text-[10px] uppercase tracking-widest text-white/50 font-black">Comprehension</p>
            <p className={`text-xl font-black tabular-nums ${compGood ? 'text-emerald-300' : 'text-white'}`}>
              {Math.round(result.comprehension_pct)}%
            </p>
          </div>
          <div className="rounded-lg bg-black/30 border border-white/10 p-3">
            <p className="text-[10px] uppercase tracking-widest text-white/50 font-black flex items-center gap-1">
              <Trophy className="w-3 h-3" /> Best
            </p>
            <p className="text-xl font-black text-white tabular-nums">{Math.round(result.best_adjusted_wpm)}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 px-3 py-1 text-amber-200 text-xs font-black">
            <Coins className="w-3.5 h-3.5" /> +{result.coins_awarded} coins
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/40 px-3 py-1 text-fuchsia-200 text-xs font-black">
            <Zap className="w-3.5 h-3.5" /> +{result.xp_awarded} XP
          </span>
          {result.chunk_size_unlocked > 2 && (
            <span className="text-[11px] uppercase tracking-widest text-cyan-300/80 font-black">
              Chunks unlocked: {result.chunk_size_unlocked}-word
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5 sm:p-6" data-testid="learn-quiz">
      <p className="eyebrow text-cyan-300 mb-1">{eyebrow || 'Comprehension check'}</p>
      <p className="text-xs text-white/50 mb-4">
        Answer all {total} questions — your score becomes your comprehension %.
      </p>
      <div className="space-y-4">
        {qBank.map((q, qi) => (
          <div key={qi} className="rounded-xl bg-black/25 border border-white/10 p-4">
            <p className="font-display text-base text-white font-black tracking-tight mb-2.5">
              {qi + 1}. {q.q}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map((opt, oi) => {
                const selected = picks[qi] === oi;
                return (
                  <button
                    key={oi}
                    type="button"
                    onClick={() => setPicks((p) => ({ ...p, [qi]: oi }))}
                    className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selected
                        ? 'bg-cyan-500/15 border-cyan-400/60 text-white'
                        : 'bg-white/[0.02] border-white/10 text-white/80 hover:bg-white/[0.05]'
                    }`}
                    data-testid={`learn-quiz-q${qi}-opt${oi}`}
                  >
                    <span className={`inline-block w-5 h-5 rounded-full mr-2 text-[10px] leading-5 text-center font-black ${
                      selected ? 'bg-cyan-400 text-slate-900' : 'bg-white/10 text-white/60'
                    }`}>
                      {String.fromCharCode(65 + oi)}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p className="mt-3 text-xs text-rose-300 flex items-center gap-1.5">
          <XCircle className="w-3.5 h-3.5" /> {error}
        </p>
      )}
      <button
        type="button"
        onClick={submit}
        disabled={submitting || answered !== total}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 px-4 py-3 text-sm font-black tracking-tight transition-colors"
        data-testid="learn-quiz-submit"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
        {submitting ? 'Scoring…' : `Submit answers (${answered}/${total})`}
      </button>
    </div>
  );
};

export default LearnQuiz;
